import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Avatar,
  Box,
  Button,
  Typography,
  Tabs,
  Tab,
  Grid,
  Paper,
  Stack,
  Badge,
} from '@mui/material';
import { hasFeature } from '../../permissions';
import PermissionDialog from '../ui/Modal/PermissionDialog';
import DisplayCard from '../ui/Card/DisplayCard';
import ConfirmDialog from '../ui/Modal/ConfirmDialog';
import UndoSnackbar from '../ui/Feedback/UndoSnackbar';
import { useUndoableDelete } from '../../hooks/useUndoableDelete';
import { deleteSpa, undoSpaDelete, fetchSpaList } from '../../api/spaApi';
import { fetchSpaBookings } from '../../api/spaApi';
import useSpaNotificationStore from '../../stores/spaNotificationStore';
import socket from '../../socket';
import { Plus } from 'lucide-react';
import SpaFormModal from './SpaFormModal';
import { useNavigate } from 'react-router-dom';
import SpaOrdersPage from '../SpaManager/SpaOrders';
import SpaSetting from '../SpaManager/SpaSetting';

const formatOpeningHours = (spa) => {
  const hours = spa?.openingHours;
  if (!Array.isArray(hours) || hours.length === 0) return 'Hours unavailable';
  const first = hours[0];
  const open = first?.open ? first.open : null;
  const close = first?.close ? first.close : null;
  if (!open || !close) return 'Hours unavailable';
  return `${open} – ${close}`;
};

export default function SpaSection({
  entries,
  setEntries,
  form,
  setForm,
  open,
  setOpen,
  handleEdit,
  handleDelete,
  propertyId,
  fetchEntries,
  allowedFeatures
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deletedCardId, setDeletedCardId] = useState(null);

  const [permOpen, setPermOpen] = useState(false);
  const [permMsg, setPermMsg] = useState('');
  const showPerm = (msg) => { setPermMsg(msg); setPermOpen(true); };

  const [activeTab, setActiveTab] = useState(0);
  // Notification badge (mirrors Restaurant)
  const newSpaCounts = useSpaNotificationStore((s) => s.newBookingCounts);
  const incSpa = useSpaNotificationStore((s) => s.incrementNewBookingCount);
  const clearSpa = useSpaNotificationStore((s) => s.clearNewBookingCount);
  const newSpaCount = useMemo(() => (propertyId ? newSpaCounts[propertyId] || 0 : 0), [newSpaCounts, propertyId]);
  // Live and derived counts (section-level badge)
  const [liveBadgeCount, setLiveBadgeCount] = useState(0);
  const [derivedBadgeCount, setDerivedBadgeCount] = useState(0);
  const [selectedSpaId, setSelectedSpaId] = useState(null);

  const [allBookings, setAllBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingsError, setBookingsError] = useState(null);

  const getLocalAF = () => {
    try {
      return JSON.parse(localStorage.getItem('allowedFeatures') || '{}');
    } catch (e) {
      console.warn('[SpaSection] Failed to parse allowedFeatures from localStorage:', e);
      return {};
    }
  };

  const AF = allowedFeatures || getLocalAF();
  const canAdd = !!(AF?.spa?.addSpa ?? hasFeature('spa.addSpa'));
  const canEdit = !!(AF?.spa?.editSpa ?? hasFeature('spa.editSpa'));
  const canDelete = !!(AF?.spa?.deleteSpa ?? hasFeature('spa.deleteSpa'));

  const navigate = useNavigate();

  const { triggerDelete, triggerUndo, showUndo, remainingTime } = useUndoableDelete({
    onDeleteApi: (item) => deleteSpa(item._id, propertyId),
    onUndoApi: (item) => undoSpaDelete(item._id, propertyId),
  });

  const loadAllBookings = useCallback(async () => {
    const pid = (propertyId && (propertyId.id || propertyId)) || null;
    if (!pid) return;
    setBookingsLoading(true);
    setBookingsError(null);
    try {
      const data = await fetchSpaBookings({ propertyId: pid });
      setAllBookings(data);
      try {
        const list = Array.isArray(data) ? data : Array.isArray(data?.bookings) ? data.bookings : [];
        const NEW = new Set(['awaiting', 'awaiting_confirmation', 'awaiting confirmation', 'pending', 'new', 'submitted', 'unconfirmed']);
        const pickStatus = (b) => {
          const s1 = b?.status ?? b?.currentStatus ?? b?.statusCode ?? b?.state;
          if (s1) return String(s1).toLowerCase();
          const hist = Array.isArray(b?.statusHistory) ? b.statusHistory : [];
          if (hist.length) {
            const last = hist[hist.length - 1];
            if (last?.status) return String(last.status).toLowerCase();
            if (last?.code) return String(last.code).toLowerCase();
          }
          return '';
        };
        const count = list.reduce((acc, b) => acc + (NEW.has(pickStatus(b)) ? 1 : 0), 0);
        setDerivedBadgeCount(count);
        if (typeof window !== 'undefined') {
          window.__EF_DEBUG_SPA__ = list;
          console.log('[SpaSection] loaded bookings:', list.length, 'derived awaiting:', count);
        }
      } catch {}
    } catch (err) {
      console.error('[SpaSection] failed to fetch all bookings', err);
      setBookingsError(err?.response?.data?.message || err?.message || 'Failed to load bookings');
    } finally {
      setBookingsLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    loadAllBookings();
  }, [loadAllBookings]);

  useEffect(() => {
    const pid = (propertyId && (propertyId.id || propertyId)) || null;
    if (activeTab === 0 && pid) {
      clearSpa(pid);            // store
      setLiveBadgeCount(0);     // live
      setDerivedBadgeCount(0);  // derived
    }
  }, [activeTab, propertyId, clearSpa]);

  useEffect(() => {
    const pid = (propertyId && (propertyId.id || propertyId)) || null;
    if (!pid) return;

    // Ensure websocket connection
    try { if (!socket.connected) socket.connect?.(); } catch (e) { console.error('[SpaSection] socket.connect error', e); }

    const joinRoom = () => {
      try {
        socket.emit('joinSpaBookingsRoom', pid);
        console.log('[socket][spa] joinSpaBookingsRoom', { propertyId: pid, connected: socket.connected, id: socket.id });
      } catch (e) {
        console.warn('[SpaSection] joinSpaBookingsRoom failed', e);
      }
    };
    if (socket.connected) joinRoom();
    socket.on('connect', joinRoom);

    // Counts snapshots (support different payload shapes)
    const onCounts = (p = {}) => {
      const n = Number(p?.pendingCount ?? p?.awaiting ?? p?.count ?? 0);
      if (!Number.isNaN(n)) setLiveBadgeCount(n);
    };

    // Creation events bump live count unless we’re on the Manage tab (already viewing)
    const onCreated = (evt = {}) => {
      setLiveBadgeCount((n) => (Number.isFinite(n) ? n + 1 : 1));
      if (activeTab !== 0 && pid) incSpa(pid);
    };

    // Upsert helpers (keep your existing ones)
    const getDoc = (payload) => (payload && payload.booking ? payload.booking : payload);
    const upsert = (incoming) => {
      const doc = getDoc(incoming);
      if (!doc || !doc._id) return;
      setAllBookings((prev) => {
        const i = prev.findIndex((b) => String(b._id) === String(doc._id));
        if (i === -1) return [doc, ...prev];
        const copy = [...prev];
        copy[i] = { ...prev[i], ...doc };
        return copy;
      });
    };
    const applyStatus = ({ bookingId, status, booking }) => {
      if (booking) return upsert({ booking });
      if (!bookingId) return;
      setAllBookings((prev) => prev.map((b) => (String(b._id) === String(bookingId) ? { ...b, status } : b)));
    };

    // Wire both namespaced and legacy events
    socket.on('spa:bookings:counts', onCounts);
    socket.on('spaBookings:counts', onCounts);

    socket.on('spa:booking:new', (payload) => { upsert(payload); onCreated(payload); });
    socket.on('spa:booking:update', ({ bookingId, change, booking }) => {
      if (booking) return upsert({ booking });
      if (bookingId && change) {
        setAllBookings((prev) => prev.map((b) => (String(b._id) === String(bookingId) ? { ...b, ...change } : b)));
      }
    });
    socket.on('spa:booking:status', applyStatus);

    socket.on('spaBooking:new', (doc) => { upsert(doc); onCreated(doc); });
    socket.on('spaBooking:updated', (doc) => upsert(doc));
    socket.on('spaBookingStatusUpdate', ({ bookingId, status, booking }) => applyStatus({ bookingId, status, booking }));

    return () => {
      socket.off('connect', joinRoom);

      socket.off('spa:bookings:counts', onCounts);
      socket.off('spaBookings:counts', onCounts);

      socket.off('spa:booking:new');
      socket.off('spa:booking:update');
      socket.off('spa:booking:status', applyStatus);

      socket.off('spaBooking:new');
      socket.off('spaBooking:updated');
      socket.off('spaBookingStatusUpdate');
    };
  }, [propertyId, activeTab, incSpa]);

  useEffect(() => {
    if (!entries?.length) {
      setSelectedSpaId(null);
      return;
    }
    if (!selectedSpaId || !entries.find((x) => x._id === selectedSpaId)) {
      setSelectedSpaId(entries[0]._id);
    }
  }, [entries, selectedSpaId]);

  const selectedSpa = useMemo(
    () => entries.find((r) => r._id === selectedSpaId) || null,
    [entries, selectedSpaId]
  );

  const bookingsForSelectedSpa = useMemo(
    () => allBookings.filter((o) => o.spaId === selectedSpaId),
    [allBookings, selectedSpaId]
  );

  const handleManageBookingsClick = (entry) => {
    setActiveTab(0);
    setSelectedSpaId(entry?._id || null);
    const pid = (propertyId && (propertyId.id || propertyId)) || null;
    if (pid) {
      clearSpa(pid);
      setLiveBadgeCount(0);
      setDerivedBadgeCount(0);
    }
  };

  const subTabSx = {
    textTransform: 'none',
    fontWeight: 600,
    color: '#6B7280',
    borderRadius: '999px',
    minHeight: 'auto',
    padding: '10px 20px',
    '&.Mui-selected': { backgroundColor: '#E9EAEC', color: '#111827' },
    '&:hover': { backgroundColor: '#F3F4F6' },
  };

  return (
    <>
      <Tabs
        value={activeTab}
        onChange={(_e, val) => setActiveTab(val)}
        aria-label="Spa management tabs"
        TabIndicatorProps={{ style: { display: 'none' } }}
        sx={{
          mt: 0,
          pt:4,
          px: 3,
          '& .MuiTabs-flexContainer': { gap: 2 },
        }}
      >
        <Tab
          disableRipple
          sx={subTabSx}
          label={
            (() => {
              const badgeVal = (liveBadgeCount ?? 0) || (derivedBadgeCount ?? 0) || newSpaCount || 0;
              return (
                <Badge badgeContent={badgeVal} color="error" overlap="circular" showZero={false}>
                  <span>Manage Bookings</span>
                </Badge>
              );
            })()
          }
        />
        <Tab disableRipple label="Manage Spa Services" sx={subTabSx} />
        <Tab disableRipple label="Settings" sx={subTabSx} />
      </Tabs>

      {activeTab === 1 && (
        <>
          <SpaFormModal
            open={open}
            onClose={() => setOpen(false)}
            initialData={form}
            onSubmit={async (data) => {
              if (data._id && !canEdit) { showPerm('You do not have permission to edit spa.'); return; }
              if (!data._id && !canAdd) { showPerm('You do not have permission to add spa.'); return; }
              try {
                if (data._id) {
                  await updateSpa(data._id, { ...data, propertyId });
                } else {
                  await createSpa({ ...data, propertyId });
                }
                setOpen(false);
                setForm({});
                fetchEntries();
              } catch (err) {
                console.error('Failed to save spa entry:', err);
              }
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, pr:3 }}>
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              aria-disabled={!canAdd}
              onClick={() => {
                if (!canAdd) { showPerm('You do not have permission to add spa.'); return; }
                setForm({});
                setOpen(true);
              }}
              sx={{
                backgroundColor: canAdd ? undefined : 'lightgray',
                color: canAdd ? undefined : 'white',
                opacity: canAdd ? 1 : 0.4,
                '&:hover': { backgroundColor: canAdd ? undefined : 'lightgray' },
                pointerEvents: 'auto',
                cursor: canAdd ? 'pointer' : 'not-allowed'
              }}
            >
              Add Spa Service
            </Button>
          </Box>

          <Grid container spacing={2} justifyContent="center" sx={{ mt: 2, px: 2 }}>
            {entries.map((entry, index) => (
              <Grid item key={entry._id || index} xs={12} sm={6} md={4} lg={3}>
                <DisplayCard
                  entry={entry}
                  onEdit={() => {
                    if (!canEdit) { showPerm('You do not have permission to edit spa.'); return; }
                    handleEdit(entry);
                  }}
                  onDelete={() => {
                    if (!canDelete) { showPerm('You do not have permission to delete spa.'); return; }
                    setDeletingId(entry._id);
                    setConfirmOpen(true);
                  }}
                  onDuplicate={async () => {
                    if (!canAdd) { showPerm('You do not have permission to add spa.'); return; }
                    const duplicateEntry = { ...entry, _id: undefined, name: `${entry.name} (Copy)` };
                    await createSpa({ ...duplicateEntry, propertyId });
                    fetchEntries();
                  }}
                  editAllowed={canEdit}
                  deleteAllowed={canDelete}
                  duplicateAllowed={canAdd}
                  onClick={() => navigate(`/spa/${entry._id}/manage`)}
                  isDeleted={entry._id === deletedCardId}
                >
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {entry.name}
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleManageBookingsClick(entry);
                      }}
                    >
                      Manage Bookings
                    </Button>
                  </Box>
                </DisplayCard>
              </Grid>
            ))}
          </Grid>

          <ConfirmDialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            onConfirm={async () => {
              if (!canDelete) { console.warn('[SpaSection] Confirm delete blocked by permission'); setConfirmOpen(false); return; }
              const item = entries.find(e => e._id === deletingId);
              if (!item) {
                setConfirmOpen(false);
                return;
              }
              try {
                await triggerDelete(item);
                setDeletedCardId(deletingId);
                setConfirmOpen(false);
                setDeletingId(null);
                fetchEntries();
              } catch (error) {
                console.error('Failed to delete spa entry:', error);
              }
            }}
            title="Confirm Delete"
            content="Are you sure you want to delete this spa entry?"
          />

          <PermissionDialog open={permOpen} onClose={() => setPermOpen(false)} message={permMsg} />

          <UndoSnackbar
            show={showUndo}
            onUndo={async () => {
              await triggerUndo();
              setDeletedCardId(null);
              fetchEntries();
            }}
            onClose={() => {
              setDeletedCardId(null);
            }}
            remainingTime={remainingTime}
            message="Spa entry deleted"
          />
        </>
      )}

      {activeTab === 0 && (
        <Box sx={{ mt: 3, px: 3 }}>
          {entries.length === 0 ? (
            <Typography color="text.secondary">No spa services available yet.</Typography>
          ) : (
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Stack
                spacing={1.5}
                sx={{ width: 320, minWidth: 320, maxWidth: 320, maxHeight: 560, overflowY: 'auto', flexShrink: 0, pr: 0.5 }}
              >
                {entries.map((entry) => {
                  const isActive = entry._id === selectedSpaId;
                  const openingLabel = formatOpeningHours(entry);
                  const cover = entry.photos?.[0] || entry.imageUrls?.[0];
                  return (
                    <Paper
                      key={entry._id}
                      elevation={isActive ? 4 : 1}
                      onClick={() => setSelectedSpaId(entry._id)}
                      sx={{
                        borderRadius: 2,
                        cursor: 'pointer',
                        border: isActive ? '2px solid #C8A86B' : '1px solid #E5E7EB',
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        backgroundColor: isActive ? 'rgba(200,168,107,0.08)' : 'white',
                      }}
                    >
                      <Avatar
                        src={cover}
                        alt={entry.name}
                        variant="rounded"
                        sx={{ width: 48, height: 48, borderRadius: 1.5 }}
                      >
                        {entry.name?.[0]?.toUpperCase() || 'S'}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={600} noWrap>{entry.name}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{openingLabel}</Typography>
                        {entry.subtitle ? <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{entry.subtitle}</Typography> : null}
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>

              <Box sx={{ flex: 1 }}>
                {selectedSpa ? (
                  <SpaOrdersPage
                    propertyId={propertyId}
                    spaId={selectedSpaId}
                    bookings={bookingsForSelectedSpa}
                    loading={bookingsLoading}
                    error={bookingsError}
                    onRefresh={loadAllBookings}
                  />
                ) : (
                  <Typography color="text.secondary" sx={{ mt: 2 }}>
                    Select a spa to view and manage its bookings.
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      )}
      {activeTab === 2 && (
        <Box sx={{ mt: 3, px: 3 }}>
          <SpaSetting propertyId={propertyId} />
        </Box>
      )}
    </>
  );
}
